import { FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Typography, Collapse } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';

import { useExpansion } from '../../../../../hooks/Expansion';
import { CardContent, CardHeader } from './Card';
import ApiService from '../../../../../ApiService';
import CollapsibleCard, { CollapsibleCardProps } from '../../../../../components/Card/CollapsibleCard/CollapsibleCard';
import CollapsibleCardExpandArea from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandArea';
import CollapsibleCardExpandButton from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandButton';
import Column from '../../../../../components/Column';
import ExpandButton from '../../../../../components/Button/ExpandButton';
import Row from '../../../../../components/Row';

import { CampaignAdGroup } from '../../../../../types/Campaign';
import { InventorySource as DspInventorySource } from '../../../../../types/Inventory';
import CampaignAdGroupInventoryChips from '../Chip/CampaignAdGroupInventoryChips';
import CampaignAdGroupInventoryDataGrid from '../DataGrid/CampaignAdGroupInventoryDataGrid';
import CampaignAdGroupInventoryDataGridFilter from '../DataGrid/CampaignAdGroupInventoryDataGridFilter';
import CampaignAdGroupInventorySummaryText from '../Text/CampaignAdGroupInventorySummaryText';
import DspInventorySourceUtils from '../../../../../utils/DspInventorySourceUtils';

interface CampaignAdGroupInventoryCardProps {
    campaignAdGroup: CampaignAdGroup;
    slotProps?: {
        collapsibleCard?: Partial<CollapsibleCardProps>;
    };
}

const CampaignAdGroupInventoryCard: FC<CampaignAdGroupInventoryCardProps> = ({ campaignAdGroup, slotProps = {} }) => {
    const { expanded, toggleExpanded } = useExpansion();

    const [targets, setTargets] = useState<DspInventorySource[]>([]);

    const targetIds: string[] = DspInventorySourceUtils.toDspInventorySourceIds(
        campaignAdGroup?.adGroupInventorySources || []
    );

    const { data, isError, isLoading } = useQuery<DspInventorySource[]>({
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/dspInventorySources', campaignAdGroup.id, targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspInventorySources({
                ids: targetIds,
            });

            return data;
        },
    });

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 5,
    });

    useEffect(() => {
        setTargets(data || []);
    }, [data]);

    useEffect(() => {
        setPaginationModel((model: GridPaginationModel) => ({ ...model, page: 0 }));
    }, [targets]);

    return (
        <CollapsibleCard defaultExpanded {...slotProps?.collapsibleCard}>
            <CardHeader
                title={
                    <Row alignItems="center" gap={1}>
                        <Typography variant="body1">Advertising Inventory</Typography>
                    </Row>
                }
                action={<CollapsibleCardExpandButton />}
            />
            <CollapsibleCardExpandArea>
                <CardContent>
                    <Column gap={3}>
                        <Column gap={2}>
                            <CampaignAdGroupInventorySummaryText items={data || []} />

                            <Row gap={1}>
                                <CampaignAdGroupInventoryChips items={data || []} />
                            </Row>
                        </Column>

                        <Column gap={5}>
                            <Box>
                                <ExpandButton
                                    variant="outlined"
                                    size="large"
                                    expand={expanded}
                                    onClick={toggleExpanded}
                                >
                                    {expanded ? 'Hide All Selections' : 'See All Selections'}
                                </ExpandButton>
                            </Box>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                {isError ? (
                                    <Alert severity="error">Failed to load inventory targeting.</Alert>
                                ) : (
                                    <Column gap={2}>
                                        <CampaignAdGroupInventoryDataGridFilter
                                            rows={data || []}
                                            onFilterChange={(filteredRows: DspInventorySource[]): void => {
                                                setTargets(filteredRows);
                                            }}
                                        />

                                        <CampaignAdGroupInventoryDataGrid
                                            rows={targets}
                                            loading={isLoading}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={(model: GridPaginationModel) =>
                                                setPaginationModel(model)
                                            }
                                        />
                                    </Column>
                                )}
                            </Collapse>
                        </Column>
                    </Column>
                </CardContent>
            </CollapsibleCardExpandArea>
        </CollapsibleCard>
    );
};

export default CampaignAdGroupInventoryCard;
